import { Component, OnInit, ViewChild, OnDestroy, AfterViewInit, ChangeDetectorRef, ElementRef, Input } from '@angular/core';
import { CurrentUserStore } from '../../user/store/current-user/current-user.store';
import { CurrentUserWorkgroupsStore } from '../../user/store/current-user-workgroups/current-user-workgroups.store';
import { CurrentUserGlobalAppsStore } from '../../user/store/current-user-global-apps/current-user-global-apps.store';
import { ShadowDomConfig } from '@app/core/directives/shadow-dom.directive';
import { UniversalNavbarComponent as ExternalUniversalNavbarComponent,
  NgxSmartModalComponent, ToastrService, ICurrentAlertNotificationState, IAlertCard,
  DropdownComponent
} from '@bssh/comp-lib';
import { ISearchInput } from '@app/shared/search-widget/search-widget.interface';
import { ActivatedRoute, Router } from '@angular/router';
import { filter, map, catchError, take, startWith, tap, delay } from 'rxjs/operators';
import { UniversalNavLinksService } from '../../core/services/universal-nav-links/universal-nav-links.service';
import { SubSink } from 'subsink';
import { IUserOrWorkgroup, IWorkgroup, WorkgroupRoleTypes } from '../../core/model/workgroup';
import { Constants } from '../../core/utilities/constants';
import { AuthorizationService } from '../../core/services/authorization/authorization.service';
import { HttpUtilityService } from '../../core/services/http-utility/http-utility.service';
import { CookieService } from 'ngx-cookie-service';
import { SearchRouteService } from '../../core/services/search/search-route.service';
import { merge, Observable, of, EmptyError, BehaviorSubject } from 'rxjs';
import { CodeFeaturesService } from '@app/core/services/user/code-features.service';
import { UserAlertNotificationService } from '@app/core/store/usernotifications/alert-notifications/user-alert-notification.service';
import { SkinnyBsshService } from '@app/core/services/skinny-bssh/skinny-bssh.service';
import { TrashItemsStore } from '@app/core/store/trash-items/trash-items.store';
import { MessageService, SearchMessageTypes } from '@app/core/services/message/message.service';
import { BsKeepAliveService } from '@app/core/services/bs-api/keep-alive/bs-keep-alive.service';
import environment from '@environments/environment';

// Radiance web components
import '@radiance-design-software/menu';
import '@radiance-design-software/menu-item';
import '@radiance-design-software/icon-button';
import '@radiance-design-software/subscription-banner';
import { IUser } from '@app/core/model/user';

/**
 * Represents the universal nav bar adapted to BSSH
 */
@Component({
  selector: 'app-bs-univ-nav-bar',
  templateUrl: './bs-univ-nav-bar.component.html',
  styleUrls: ['./bs-univ-nav-bar.component.scss'],
  providers: [ SearchRouteService]
})

export class BsUnivNavBarComponent implements OnInit, OnDestroy, AfterViewInit {
  public menuOptions: any;
  public searchRoute: string;
  public searchRouteService = new SearchRouteService();
  public searchResults: any;
  public archiveEnabled = false;
  public runZippingEnabled = false;
  private isBioSampleRegistryEnabled  = true;
  public trashBadge: string;
  public userHasActiveIcaSubscription = false;

  // Radiance header navigation properties
  public legacyNavbarEnabled = false;
  // False until BSSH's real workgroup has been pushed into rad-header-navigation. Guards
  // blankRadWorkgroupLabel() so a late blanking call can never wipe an already-correct label.
  public workgroupResolved = false;
  public subscriptionBannerEnabled = false;
  public currentUser$: Observable<IUser>;
  public userInitials: string = '';
  public userName: string = '';
  public userEmail: string = '';
  // Backwards-compat: PSM's /do/globalAdmin/domainApp/search endpoint only works for
  // enterprise accounts on the `platform.login.*` vhost, not the `login.*` vhost (SET-16948).
  public platformBaseUrl: string = (environment.platformUrl || '')
    .replace(/^https:\/\/login\./, 'https://platform.login.');
  public clientId: string = environment.clientId || '';
  public platformDomain: string = 'platform';

  // Subscription banner environment: the component uses this for its messages-config
  // fetch and mock-state safety. platform-base-url drives the actual subscriptions
  // endpoint, but environment is still required (see BASE-89108 plan).
  public subscriptionBannerEnvironment: string = environment.production ? 'prod' : 'test';

  public appTitle: string = 'Sequence Hub';

  // Narrow-viewport fix injected into the Radiance header's shadow root via [appShadowStyle].
  // At <=800px the header bar grid is `minmax(200px,40%) minmax(200px,60%)`, a hard 400px floor
  // that pushes the right-column action icons (incl. the profile/sign-out icon) off-screen below
  // ~400px. The `.rad-header-navigation__bar` container is NOT an exposed shadow part, so a plain
  // media query / ::part() can't reach it. This relaxes the left column to its content width so
  // the icons fit down to ~350px. Scoped to <=420px only; wider layouts are untouched.
  public readonly headerNavShadowStyles: string = `
    @media (max-width: 490px) {
      .rad-header-navigation__bar,
      .rad-header-navigation__bar-compact {
        grid-template-columns: minmax(0, auto) 1fr !important;
      }
      .rad-header-navigation__global-utilities {
        padding-right: 0 !important;
      }
    }
  `;
  // Un-versioned, manually uploaded to s3://basespace-cdn-public-east/sites/bssh-website/.
  // To update, file an EOS ticket (see EOS-88964 for the original upload). Mirrors the
  // favicon pattern in src/index.html.
  public appWhitelistUrl: string =
    'https://static.basespace.illumina.com/sites/bssh-website/product-whitelist.json';
    public termsUrl = this.universalNavLinksService.softwareSubscriptionAgreementUrl;

  // Legacy navbar branding, bound declaratively in the template. These must NOT be assigned
  // imperatively via the ViewChild: the legacy navbar is behind *ngIf="legacyNavbarEnabled",
  // which is false until code features resolve, so it does not exist during ngAfterViewInit.
  public logoUrl = this.universalNavLinksService.logoUrl;
  public navBarTitle = this.universalNavLinksService.navBartitle;
  public titleLink = this.universalNavLinksService.titleLink;
  public logoLinkNewWindow = this.universalNavLinksService.logoLinkNewWindow;
  public titleLinkNewWindow = this.universalNavLinksService.titleLinkNewWindow;

  public regionCode: string = '';
  private loggedInUserId: string = null; // Stores the logged-in user ID for "Personal" workgroup mapping
  private bsshWorkgroups: IWorkgroup[] = []; // Cache of BSSH workgroups for ID mapping

  // Admin Console link properties
  public showAdminConsoleLink: boolean = false;
  public adminConsoleUrl: string = '';

  // Shadow DOM config for user profile header in the profile menu
  public userProfileHeaderShadowConfig: ShadowDomConfig | null = null;

  // Config for profile dropdown (panelClass must be passed via config object)
  public radProfileDropdownConfig = {
    panelClass: 'rad-profile-dropdown',
    width: '280px',
    minWidth: '280px',
    height: 'auto',
    maxHeight: 'none'
  };

  // Config for notifications dropdown (panelClass must be passed via config object)
  public radNotificationsDropdownConfig = {
    panelClass: 'rad-notifications-dropdown',
    width: '38.6rem',
    minWidth: '38.6rem',
    height: 'auto',
    maxHeight: '450px'
  };

  // Config for help dropdown (panelClass must be passed via config object)
  public radHelpDropdownConfig = {
    panelClass: 'rad-help-dropdown',
    width: '220px',
    minWidth: '220px',
    height: 'auto',
    maxHeight: 'none'
  };

  @ViewChild('radProfileDropdownMenu', { static: false }) radProfileDropdownMenu: DropdownComponent;
  @ViewChild('radHeaderNav', { static: false }) radHeaderNav: ElementRef;
  @ViewChild('radNotificationsDropdownMenu', { static: false }) radNotificationsDropdownMenu: DropdownComponent;
  @ViewChild('radHelpDropdownMenu', { static: false }) radHelpDropdownMenu: DropdownComponent;

  // Notifications for radiance header
  public isNotificationsOpen = false;
  public alertNotifications$: Observable<ICurrentAlertNotificationState>;

  // Search for radiance header
  public isRadSearchOpen = false;
  public alertNotificationsLoading$ = new BehaviorSubject<boolean>(true);
  public alertNotificationsMessage: string = '';

  public searchWidgetParameters = {
    overlayTop: '52px',
    closeOnSelect: 'true',
    requester: (inputValue: string) => {
      return this.searchRouteService.getSearchAreas();
    },
    resultsMapper: (value, stringData) => {
      return {
        label: value.name,
        action: value.type,
        value: value.value || stringData
      };
    },
    onSelectAction: (data: ISearchInput) => {
      this.setSearchRoute(data);
    }
  };

  onSelectAction(data: ISearchInput) {
    this.setSearchRoute(data);
  }

  private subs: SubSink = new SubSink();

  private _navBar: ExternalUniversalNavbarComponent;

  public get navBar(): ExternalUniversalNavbarComponent {
    return this._navBar;
  }

  /**
   * The legacy navbar lives behind *ngIf="legacyNavbarEnabled", which is false until code
   * features resolve — so it is absent during ngAfterViewInit and appears later. Setter-based
   * query lets its imperative setup run the moment it actually shows up.
   */
  @ViewChild(ExternalUniversalNavbarComponent, { static: false })
  set navBarRef(navBar: ExternalUniversalNavbarComponent) {
    if (!navBar || navBar === this._navBar) {
      return;
    }
    this._navBar = navBar;
    this.initializeLegacyNavBar();
  }

  // Todo: Make the invocation of the modal more imperative and dynamic.
  @ViewChild('subscriptionErrorModal', { static: true }) private subscriptionErrorModal: NgxSmartModalComponent;


  @Input()
  get transparent(): boolean { return this._transparent; }
  set transparent(value: boolean) {
    this._transparent = value;
  }
  private _transparent = null;
  private isSkinnyBssh: boolean = false;

  // Cached values for navBar input properties (to avoid creating new references on every change detection)
  private _navBarApplicationLinkBottom: any = null;
  private _navBarProfileLinks: any[] = [];
  private _navBarLinks: any[] = [];
  private _navBarUserService: any = null;
  private _navBarUserWorkgroupService: any = null;
  private _navBarUserApplicationsService: any = null;
  private _navBarUserAlertNotificationService: any = null;

  // Simple getters that return cached values
  get navBarApplicationLinkBottom(): any {
    return this._navBarApplicationLinkBottom;
  }

  get navBarProfileLinks(): any[] {
    return this._navBarProfileLinks;
  }

  get navBarLinks(): any[] {
    return this._navBarLinks;
  }

  // Simple getters for cached service wrappers
  get navBarUserAlertNotificationService(): any {
    return this._navBarUserAlertNotificationService;
  }

  get navBarUserApplicationsService(): any {
    return this._navBarUserApplicationsService;
  }

  get navBarUserService(): any {
    return this._navBarUserService;
  }

  get navBarUserWorkgroupService(): any {
    return this._navBarUserWorkgroupService;
  }


  constructor(
    private userStore: CurrentUserStore,
    private userWorkgroupsStore: CurrentUserWorkgroupsStore,
    private userGlobalAppsStore: CurrentUserGlobalAppsStore,
    private router: Router,
    private route: ActivatedRoute,
    private universalNavLinksService: UniversalNavLinksService,
    private toastService: ToastrService,
    private authService: AuthorizationService,
    private httpUtilityService: HttpUtilityService,
    private changeDetectorRef: ChangeDetectorRef,
    private _elementRef: ElementRef,
    private codeFeaturesService: CodeFeaturesService,
    private userAlertNotificationService: UserAlertNotificationService,
    private skinnyBsshService: SkinnyBsshService,
    private trashItemsStore: TrashItemsStore,
    private messageService: MessageService,
    private cookieService: CookieService,
    private keepAliveService: BsKeepAliveService
    ) {
    // Initialize cached navbar service wrappers once (to avoid creating new references on every change detection)
    this.initializeCachedNavbarValues();
  }

  /**
   * Initialize cached values for navbar inputs to prevent flashing from new object references
   * These are created once and reused, so Angular change detection doesn't see them as changed
   */
  private initializeCachedNavbarValues(): void {
    // Cache static navbar values
    this._navBarApplicationLinkBottom = this.universalNavLinksService.getWaffleMenuApplicationsFooterLink();
    this._navBarProfileLinks = this.universalNavLinksService.getUserProfileLinkItems();
    this._navBarLinks = this.universalNavLinksService.getNavItems(true);

    // Cache service wrappers (these objects contain observables, created once)
    this._navBarUserService = {
      stateChanged: this.userStore.stateChanged,
      loading$: this.userStore.loading$
    };

    this._navBarUserWorkgroupService = {
      stateChanged: this.userWorkgroupsStore.stateChanged,
      loading$: this.userWorkgroupsStore.loading$
    };

    this._navBarUserApplicationsService = {
      stateChanged: this.universalNavLinksService.getGlobalAppItemsState(),
      loading$: this.userGlobalAppsStore.loading$
    };

    if (this.userAlertNotificationService && this.userAlertNotificationService.userAlertNotificationStore) {
      this._navBarUserAlertNotificationService = {
        stateChanged: this.userAlertNotificationService.getUserAlertNotificationStoreState(),
        loading$: this.userAlertNotificationService.userAlertNotificationStore.loading$
      };
    }
  }

  /**
   * OnInit Life cycle hook
   */
  ngOnInit(): void {
    // Set up current user observable
    this.currentUser$ = this.userStore.stateChanged.pipe(
      filter(state => state != null && state.currentUser != null),
      map(state => state.currentUser)
    );

    // Derive user initials from the personal (logged-in) account - not the current workgroup
    // These should remain constant regardless of workgroup switches
    this.subs.sink = this.currentUser$.subscribe(user => {
      if (user) {
        // Use LoggedInUser (personal account) for profile display if available
        const personalUser = user.LoggedInUser || user;
        if (personalUser.Name && !this.userInitials) {
          this.userName = personalUser.Name;
          this.userEmail = personalUser.Email || '';
          this.userInitials = this.getInitials(personalUser.Name);
        }

        // Store logged-in user ID for "Personal" workgroup mapping
        if (user.LoggedInUser && user.LoggedInUser.Id) {
          this.loggedInUserId = user.LoggedInUser.Id;
        }

        // Determine if Admin Console link should be shown
        if (user.Roles) {
          this.showAdminConsoleLink = user.Roles.some(role =>
            role === WorkgroupRoleTypes.DomainAdmin ||
            role === WorkgroupRoleTypes.Admin ||
            role === WorkgroupRoleTypes.Owner
          );
          if (this.showAdminConsoleLink) {
            this.adminConsoleUrl = this.httpUtilityService.getWorkgroupAdminConsoleUrl(user.IsPublicDomainUser);
          }
        }
      }
    });

    // Check if Skinny BSSH and update cached nav links accordingly
    this.isSkinnyBssh = this.skinnyBsshService.isSkinnyBssh();
    this._navBarLinks = this.universalNavLinksService.getNavItems(!this.isSkinnyBssh);

    // Static config for rad-header-navigation — set once so the web component has correct
    // attributes from the moment *ngIf inserts it into the DOM (no race with connectedCallback)
    if (this.httpUtilityService.isLocalEnvironment()) {
      this.platformDomain = 'platform';
    } else {
      const psToken = this.cookieService.get(Constants.Auth.authpsToken);
      const psTokenDomain = this.httpUtilityService.getDomainFromPsToken(psToken);
      this.platformDomain = psTokenDomain || this.httpUtilityService.getDomainFromCurrentRequestUrl();
    }
    this.regionCode = this.universalNavLinksService.getCurrentRegionCode();

    // Notifications observable for radiance header (always wired — no-ops when *ngIf hides it)
    this.alertNotifications$ = this.userAlertNotificationService.getUserAlertNotificationStoreState().pipe(
      filter(state => state != null && state.currentUserAlertNotifications != null)
    );

    this.subs.sink = this.userAlertNotificationService.userAlertNotificationStore.loading$.pipe(
      startWith(true),
    ).subscribe({
      next: (loading) => {
        this.alertNotificationsLoading$.next(loading);
      }
    });

    this.subs.sink = this.alertNotifications$.pipe(
      filter(state => state != null && state.currentUserAlertNotifications != null)
    ).subscribe({
      next: (state) => {
        const notifications = state.currentUserAlertNotifications;
        if (notifications && notifications.alertCards && notifications.alertCards.length === 0) {
          this.alertNotificationsMessage = 'No notifications';
        } else {
          this.alertNotificationsMessage = '';
        }
      }
    });

    // Cache BSSH workgroups for mapping Platform org IDs to BSSH workgroup IDs
    this.subs.sink = this.userWorkgroupsStore.stateChanged.pipe(
      filter(state => state != null && state.currentUserWorkgroups != null && state.currentUserWorkgroups.Items != null)
    ).subscribe({
      next: state => {
        this.bsshWorkgroups = state.currentUserWorkgroups.Items as IWorkgroup[];
      }
    });

    // Update active workgroup in rad-header-navigation when user state changes
    // delay(0) ensures Angular change detection has rendered the *ngIf element before we
    // try to call setActiveWorkgroup (the flag-flip subscription fires in the same emission)
    this.subs.sink = this.userStore.stateChanged.pipe(
      filter(state => state != null && state.currentUser != null),
      map(state => state.currentUser),
      delay(0)
    ).subscribe({
      next: (currentUser) => {
        const workgroupType = this.mapCurrentUserToWorkgroupType(currentUser);
        if (workgroupType && this.radHeaderNav && this.radHeaderNav.nativeElement && this.radHeaderNav.nativeElement.setActiveWorkgroup) {
          // Hack: First set to a dummy workgroup with unique ID to force component to recognize change
          const dummyWorkgroup = {
            organization: {
              id: 'dummy-' + Date.now(),
              name: workgroupType.organization.name
            }
          };
          // Set before dispatching: the dummy already carries the real name, so the label is
          // correct from here on and any queued blankRadWorkgroupLabel() must not wipe it.
          this.workgroupResolved = true;
          this.radHeaderNav.nativeElement.setActiveWorkgroup(dummyWorkgroup);

          // Then immediately set to the correct workgroup
          setTimeout(() => {
            this.radHeaderNav.nativeElement.setActiveWorkgroup(workgroupType);
          }, 0);
        } else {
          // Couldn't push a workgroup (element not upgraded yet, or no user). Stop blanking so the
          // label can't stay empty forever — the component's own value is better than nothing.
          this.workgroupResolved = true;
        }
      }
    });

    // Keep old navbar's profileLinks in sync with current user
    this.subs.sink = this.userStore.stateChanged.pipe(
      filter(state => state != null && state.currentUser != null)
    ).subscribe(state => {
      this._navBarProfileLinks = this.universalNavLinksService.getUserProfileLinkItems(state.currentUser);
    });

    // React to code feature flag changes (including after account switches)
    this.subs.sink = this.userStore.stateChanged.pipe(
      filter(state => state != null && state.currentUserCodeFeatures != null)
    ).subscribe(() => {
      this.legacyNavbarEnabled = this.codeFeaturesService.legacyNavbarEnabled;
      this.subscriptionBannerEnabled = this.codeFeaturesService.subscriptionBannerEnabled;
    });

    // Common subscriptions needed regardless of which navbar is used
    this.initializeCommonSubscriptions();
  }

  /**
   * Initialize subscriptions needed by both navbar types
   */
  private initializeCommonSubscriptions(): void {
    // Load user alert notifications for notifications dropdown
    this.userAlertNotificationService.loadUserAlertNotificationList('Invite', 0, 20, 'DateModified', 'Desc');

    // Refresh notifications on context change
    this.subs.sink = this.userStore.contextChanged$.subscribe({
      next: () => {
        this.userAlertNotificationService.loadUserAlertNotificationList('Invite', 0, 20, 'DateModified', 'Desc', true);
      }
    });

    // Subscribe to trash badge count
    this.subs.sink = this.trashItemsStore.stateChanged.pipe(
      filter((state) => !!state && !isNaN(state.trashItemsCount))
    ).subscribe((state) => {
      this.trashBadge = this.formatTrashBadgeCount(state.trashItemsCount);
    });
  }

  public deleteUserAlertNotification($event: IAlertCard){
    this.userAlertNotificationService.deleteUserAlertNotification($event);
  }

  private formatTrashBadgeCount(count: number): string | null {
    if(count === 0) {
      return null;
    } else if(count < 100) {
      return `${count}`;
    } else {
      return `99+`;
    }
  }

  ngAfterViewInit() {
    // Clear the Radiance header's self-resolved "Personal" label before it can be seen.
    // Called twice on purpose: rad-header-navigation is a custom element, so its ngOnInit (which
    // dispatches the Personal fallback) may run either side of our ngAfterViewInit. The macrotask
    // call catches the case where its dispatch lands after our first attempt.
    this.blankRadWorkgroupLabel();
    setTimeout(() => this.blankRadWorkgroupLabel(), 0);

    this.subscribeToCurrentUserStore();

    this.changeDetectorRef.detectChanges();
  }

  /**
   * Switches the context to another user/workgroup
   * @param userOrWorkgroup The new workgroup we're switching to
   */
  switchUserContext(userOrWorkgroup: IUserOrWorkgroup): void {
    this.userStore.changeUserContext(userOrWorkgroup.Id);
  }

  titleClicked(): void {
    this.router.navigate(['/']);
  }

  /**
   * Handles skip link click to focus main content area
   */
  onSkipLinkClicked(): void {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
    }
  }

  /**
   * Handles workgroupChanged event from rad-header-navigation
   * Prevents default behavior and calls BSSH's context switch API
   * @param event CustomEvent with workgroup detail
   */
  onRadWorkgroupChanged(event: CustomEvent): void {
    // Prevent rad-header-navigation from completing the change automatically
    event.preventDefault();

    const workgroup = event.detail;

    // Map WorkgroupType to BSSH workgroup ID and call switchUserContext
    const workgroupId = this.mapWorkgroupToId(workgroup);
    this.userStore.changeUserContext(workgroupId);

    // Close the context switcher dropdown after initiating the switch
    if (this.radHeaderNav && this.radHeaderNav.nativeElement && this.radHeaderNav.nativeElement.closeProductMenu) {
      this.radHeaderNav.nativeElement.closeProductMenu();
    }
  }

  /**
   * Handles the authFailure event from rad-header-navigation.
   *
   * The Radiance header's own HTTP stack swallows 401s from its switcher fetches and never
   * passes through any BaseSpace interceptor, so an expired session would otherwise leave the
   * switcher menu hung on "Loading" (BASE-89190). We re-validate the session with a lightweight
   * keepalive check (no data fetch). When the session is expired that check returns 401, which
   * the existing BsApiUnauthorizedInterceptor catches and uses to log the user out — keeping
   * redirect policy in the interceptor. The header may emit authFailure a few times in quick
   * succession; firing keepalive more than once is harmless.
   */
  onRadAuthFailure(event: CustomEvent): void {
    this.subs.sink = this.keepAliveService.keepSessionAlive().pipe(
        catchError(() => of(null))
    ).subscribe();
  }

  searchAction(event) {
    // No-op: lib-universal-navbar handles search internally via searchWidgetConfig
  }

  /**
   * Method to invoke when the 'help' action icon is clicked.
   * @param data The $event
   */
  helpAction(event) {
    /**
     * To do: Christopher has to update walkme to overlay a transparent
     * element on top of the help icon and that is controlling the walking interaction
    */
    // navigating help to an fallback help page when the script isn't present
    // when script is present then walkme will take over
    window.open(this.universalNavLinksService.bsHelpUrl, "_blank");

    if (this.radHelpDropdownMenu && this.radHelpDropdownMenu.showing) {
      this.radHelpDropdownMenu.hide();
    }
  }

  /**
   * Toggles the help dropdown visibility for the radiance header.
   * Note: when WalkMe is loaded it overlays the help icon (class walkme-trigger--panel)
   * and intercepts the click before this handler runs, so this toggle only fires
   * for users without WalkMe enabled.
   * @param event The click event
   */
  toggleHelpDropdown(event: Event): void {
    this.toggleDropdown(this.radHelpDropdownMenu);
  }

  /**
   * Shared show/hide toggle for the radiance header lib-dropdown menus
   * (notifications, profile, help). Safe to call when the dropdown ref is not yet set.
   * @param dropdown The DropdownComponent to toggle
   */
  private toggleDropdown(dropdown: DropdownComponent): void {
    if (!dropdown) {
      return;
    }
    if (dropdown.showing) {
      dropdown.hide();
    } else {
      dropdown.show();
    }
  }

  /**
   * Method to invoke when the 'trash' action icon is clicked.
   * @param data The $event
   */
  trashAction(event) {
    // To do: plugin logic here, when trash is ready to be implemented
    this.router.navigate([this.universalNavLinksService.trashUrl]);
  }

  /**
   * Toggles the search widget visibility for radiance header
   * Sends a message to app.component where the search widget is now located
   */
  toggleRadSearch(): void {
    this.messageService.sendMessage({ type: SearchMessageTypes.ToggleSearch });
  }

  /**
   * Hides the search widget for radiance header
   * This is kept for backwards compatibility but search close is now handled in app.component
   */
  onRadHideSearch(): void {
    this.messageService.sendMessage({ type: SearchMessageTypes.CloseSearch });
  }

  /**
   * Method to invoke when the 'notifications' action icon is clicked.
   * Opens the notifications dropdown menu for radiance header
   * @param event The click event
   */
  notificationsAction(event) {
    if (!this.legacyNavbarEnabled && this.radNotificationsDropdownMenu) {
      this.toggleNotificationsDropdown();
    }
  }

  /**
   * Toggles the notifications dropdown visibility
   */
  toggleNotificationsDropdown(): void {
    if (this.radNotificationsDropdownMenu) {
      this.isNotificationsOpen = !this.isNotificationsOpen;
      this.toggleDropdown(this.radNotificationsDropdownMenu);
    }
  }

  /**
   * Removes/dismisses a notification from the radiance header dropdown
   * @param notification The notification to remove
   * @param index The index of the notification in the array
   */
  removeRadNotification(notification: IAlertCard, index?: number): void {
    this.userAlertNotificationService.deleteUserAlertNotification(notification);
  }

  /**
   * Navigates to a notification route
   * @param route The route to navigate to
   */
  navigateToNotification(route: string): void {
    if (route && route !== '#' && route !== '') {
      this.router.navigate([route]);
    }
  }

  /**
   * A temporary log method for actions to log
   * Todo: Remove when all actions have been implemented.
   * @param data The $event
   */
  log(data) {
    for (const key in data) {
      if (data.hasOwnProperty(key)) {
        const element = data[key];
        console.log(`Callback for ${key} has status ${element}`);
      }
    }
  }

  /**
   * Logs out user
   * @param data The $event
   */
  logOut() {
    this.authService.logout(true);
  }

  /**
   * Opens the radiance profile menu
   * @param event The click event from the trigger button
   */
  openRadProfileMenu(event: Event): void {
    this.toggleDropdown(this.radProfileDropdownMenu);
  }

  /**
   * Maps a WorkgroupType from rad-header-navigation to a BSSH workgroup ID
   * Platform API uses ExternalProviderId (UUID), but BSSH API expects its own workgroup ID
   * @param workgroup The WorkgroupType from rad-header-navigation
   * @returns The workgroup ID string for BSSH API, or null if invalid
   */
  private mapWorkgroupToId(workgroup: any): string | null {
    if (!workgroup || !workgroup.organization) {
      return null;
    }

    const orgName = workgroup.organization.name;
    const platformOrgId = workgroup.organization.id; // This is the Platform/ExternalProviderId (UUID)

    // For "Personal" workgroup, use the logged-in user's ID
    if (orgName === 'Personal') {
      return this.loggedInUserId;
    }

    // Look up the BSSH workgroup by matching ExternalProviderId with Platform org ID
    const matchingWorkgroup = this.bsshWorkgroups.find(wg => wg.ExternalProviderId === platformOrgId);
    if (matchingWorkgroup) {
      return matchingWorkgroup.Id;
    }

    return null;
  }

  /**
   * Clears rad-header-navigation's workgroup label until BSSH tells us the real one.
   */
  private blankRadWorkgroupLabel(): void {
    if (this.workgroupResolved) {
      return;
    }
    const headerNavEl = this.radHeaderNav && this.radHeaderNav.nativeElement;
    if (headerNavEl && headerNavEl.setActiveWorkgroup) {
      headerNavEl.setActiveWorkgroup({ organization: { id: null, name: null } });
    }
  }

  /**
   * Maps current user to WorkgroupType format for rad-header-navigation
   * @param user The current user from BSSH
   * @returns WorkgroupType object for rad-header-navigation
   */
  private mapCurrentUserToWorkgroupType(user: any): any {
    if (!user) {
      return null;
    }

    // Check if this is the personal account (logged-in user ID matches current user ID)
    const isPersonal = user.LoggedInUser && user.Id === user.LoggedInUser.Id;

    return {
      organization: {
        id: user.Id,
        name: isPersonal ? 'Personal' : user.Name
      }
    };
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private setSearchRoute(data: ISearchInput) {
    const queryParameters = this.searchRouteService.setSearchQuery(data.value, data.action);
    this.router.navigate(['search'],
    {
      queryParams: queryParameters,
      replaceUrl: true,
      // can be removed after old site is decommissioned
      state:
      {
        ['initialUrl']: this.router.url
      }
    });
  }

  /**
   * Runs the legacy navbar's imperative setup. Called from the ViewChild setter, so it fires
   * whenever the legacy navbar appears rather than at a fixed lifecycle point.
   * Note: logoUrl, title, titleLink, links, profileLinks and applicationLinkBottom are all
   * supplied via template bindings, so only genuinely imperative work belongs here.
   */
  private initializeLegacyNavBar(): void {
    // Deferred: setActive() mutates links[].active and pageTitle, which would otherwise be
    // changed during the same change-detection pass that created the navbar.
    setTimeout(() => this.setCurrentlyActiveNavBarMenuItem(), 0);
  }

  /**
   * Sets the currently active menu option, when directly navigated to the url
   */
  private setCurrentlyActiveNavBarMenuItem() {
    if (!this.navBar) {
      return;
    }
    const currentUrl = this.httpUtilityService.getPathFromCurrentRequestUrl();
    this.navBar.setActive(currentUrl);
  }

  /**
   * subscribe To CurrentUserStore.
   */
  private subscribeToCurrentUserStore() {
    // this.errorModal.setData
    this.subs.sink = this.userStore.stateChanged.pipe(
      filter(state => state != null)
    ).subscribe({
      next: (state) => {
         // Listens to errors from the user store and shows a modal/toast accordingly.
        if (state.currentUserStateError != null) {
          // If the error is because of an invalid subscription problem, show a modal with helpful instructions.
          if (state.currentUserStateError.error.ErrorCode === Constants.inactiveSubscriptionErrorCode) {
            this.subscriptionErrorModal.open(true);
          }
        }

        if (state.currentUser != null && state.currentUserCodeFeatures != null) {
          // Store logged-in user ID for "Personal" workgroup mapping
          if (state.currentUser.LoggedInUser && state.currentUser.LoggedInUser.Id) {
            this.loggedInUserId = state.currentUser.LoggedInUser.Id;
          }

          // Update legacy navbar opt-out flag when code features load
          this.legacyNavbarEnabled = this.codeFeaturesService.legacyNavbarEnabled;
          this.subscriptionBannerEnabled = this.codeFeaturesService.subscriptionBannerEnabled;

          // walk-me still needs this
          this.isBioSampleRegistryEnabled = this.codeFeaturesService.bioSampleRegistryEnabled;

          // Update cached navbar links (input binding will propagate to navbar)
          // we're no longer disabling the Biosamples menu link based on code feature, but skinnyBssh may limit what's visible
          this._navBarLinks = this.universalNavLinksService.getNavItems(!this.isSkinnyBssh);

          // used for walkme only, always hidden: adds hidden element to the DOM when the archive code feature is enabled.
          this.archiveEnabled = this.codeFeaturesService.archivingIsEnabled;

          // used for walkme only, always hidden: adds hidden element to the DOM when the run zipping code feature is enabled.
          this.runZippingEnabled = this.codeFeaturesService.runZippingIsEnabled;

          // used for walkme only, always hidden: adds hidden element to the DOM when the user has an active ICA subscription
          this.userHasActiveIcaSubscription = state.currentUser.Subscription != null
                                              && state.currentUser.Subscription.HasActiveIcaSubscription === true;
        }
      }
    });
  }

  /**
   * Derives initials from a user's full name.
   * Takes the first character of the first and last name (if available).
   * @param name The user's full name
   * @returns The user's initials (max 2 characters)
   */
  private getInitials(name: string): string {
    if (!name) {
      return '';
    }
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) {
      return parts[0].charAt(0).toUpperCase();
    }
    return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
  }
}
